import type { AnalyticsEvent } from "@quiz/quiz-core";

const STORAGE_KEY = "quiz:analytics-events:v1";
const MAX_QUEUE_SIZE = 500;
const BATCH_SIZE = 25;

let queue: AnalyticsEvent[] = loadQueue();
let flushTimer: number | undefined;
let flushing = false;
let listenersAttached = false;

export function enqueueAnalyticsEvent(event: AnalyticsEvent): void {
  queue.push(event);
  if (queue.length > MAX_QUEUE_SIZE) queue = queue.slice(-MAX_QUEUE_SIZE);
  persistQueue();
  attachListeners();
  if (queue.length >= 10) void flushAnalyticsEvents();
  else scheduleFlush();
}

export async function flushAnalyticsEvents(keepalive = false): Promise<void> {
  if (flushing || queue.length === 0) return;
  flushing = true;
  const batch = queue.slice(0, BATCH_SIZE);
  try {
    const response = await fetch("/api/events", {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify({ events: batch }),
      credentials: "same-origin",
      keepalive
    });
    if (!response.ok) return;
    const sent = new Set(batch.map((event) => event.event_id));
    queue = queue.filter((event) => !sent.has(event.event_id));
    persistQueue();
  } catch {
    return;
  } finally {
    flushing = false;
  }
  if (queue.length > 0 && !keepalive) scheduleFlush(150);
}

function scheduleFlush(delay = 900): void {
  if (flushTimer !== undefined) window.clearTimeout(flushTimer);
  flushTimer = window.setTimeout(() => {
    flushTimer = undefined;
    void flushAnalyticsEvents();
  }, delay);
}

function attachListeners(): void {
  if (listenersAttached) return;
  listenersAttached = true;
  window.addEventListener("online", () => void flushAnalyticsEvents());
  window.addEventListener("pagehide", () => void flushAnalyticsEvents(true));
}

function loadQueue(): AnalyticsEvent[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]");
    return Array.isArray(parsed) ? parsed.slice(-MAX_QUEUE_SIZE) as AnalyticsEvent[] : [];
  } catch {
    return [];
  }
}

function persistQueue(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  } catch {
    queue = queue.slice(-100);
  }
}
